import type { TranslationKey } from '../../i18n'
import type { Lot, LotStage } from '../../types/lot'

export type LotFormAction = 'create' | 'update' | 'move' | 'transfer'
export type LotFormIntent = 'default' | 'transfer'
export type LotTransferIntentState = 'inactive' | 'selectFacility' | 'selectUnit' | 'selectSite' | 'sameLocation' | 'moveInstead' | 'processingSiteSameFacility' | 'ready'
export type LotTransferTargetLocationType = 'production-unit' | 'farm' | 'processing-site'

export type LotFormActionPayload = {
  lotKey: string
  farmId: string
  productionUnitId: string
  stage: LotStage
  species?: string
  stockingDate?: string
  harvestDate?: string
  biomassKg?: number
}

type LotFormActionOptions = {
  targetLocationType?: LotTransferTargetLocationType
  processingSiteId?: string
}

type BuildLotFormPresentationParams = {
  sourceLot: Lot | null
  intent: LotFormIntent
  payload: LotFormActionPayload
  formFarmId: string
  formProductionUnitId: string
  formProcessingSiteId?: string
  targetLocationType?: LotTransferTargetLocationType
}

export type LotFormPresentation = {
  action: LotFormAction
  titleKey: TranslationKey
  submitKey: TranslationKey
  noticeKey: TranslationKey | null
  locationSummary: {
    currentFarmId: string
    currentProductionUnitId: string
    targetFarmId: string
    targetProductionUnitId: string
    targetLocationType: LotTransferTargetLocationType
    targetLocationId: string
  } | null
}

export const getLotTransferIntentSubmitErrorKey = (
  state: LotTransferIntentState
): TranslationKey | null => {
  if (state === 'sameLocation') {
    return 'lots.error.transferSameLocation'
  }

  if (state === 'processingSiteSameFacility') {
    return 'lots.error.transferProcessingSiteSameFacility'
  }

  return null
}

export const isLotTransferIntentSubmitDisabled = (
  state: LotTransferIntentState
): boolean => {
  if (state === 'selectSite') {
    return true
  }

  return Boolean(getLotTransferIntentSubmitErrorKey(state))
}

const normalizeDateField = (value?: string): string => value ? value.slice(0, 10) : ''

export const isPureLocationOnlyEdit = (sourceLot: Lot, payload: LotFormActionPayload): boolean => (
  sourceLot.lotKey === payload.lotKey &&
  sourceLot.stage === payload.stage &&
  (sourceLot.species?.trim() || undefined) === payload.species &&
  normalizeDateField(sourceLot.stockingDate) === (payload.stockingDate ?? '') &&
  normalizeDateField(sourceLot.harvestDate) === (payload.harvestDate ?? '') &&
  sourceLot.biomassKg === payload.biomassKg
)

export const getLotFormAction = (
  sourceLot: Lot | null,
  payload: LotFormActionPayload,
  options?: LotFormActionOptions
): LotFormAction => {
  if (!sourceLot) {
    return 'create'
  }

  if (!isPureLocationOnlyEdit(sourceLot, payload)) {
    return 'update'
  }

  if (sourceLot.farmId !== payload.farmId) {
    return 'transfer'
  }

  if ((options?.targetLocationType ?? 'production-unit') === 'processing-site') {
    const targetProcessingSiteId = options?.processingSiteId?.trim() ?? ''
    const currentLocationType = sourceLot.locationType ?? 'production-unit'
    const currentLocationId = currentLocationType === 'processing-site'
      ? (sourceLot.locationId?.trim() ?? '')
      : ''

    if (currentLocationType !== 'processing-site' || currentLocationId !== targetProcessingSiteId) {
      return 'move'
    }

    return 'update'
  }

  if (sourceLot.productionUnitId !== payload.productionUnitId) {
    return 'move'
  }

  return 'update'
}

export const getLotTransferIntentState = (params: {
  sourceLot: Lot | null
  intent: LotFormIntent
  payload: Pick<LotFormActionPayload, 'farmId' | 'productionUnitId'> & { processingSiteId?: string }
  targetLocationType?: LotTransferTargetLocationType
}): LotTransferIntentState => {
  if (params.intent !== 'transfer' || !params.sourceLot) {
    return 'inactive'
  }

  const targetLocationType = params.targetLocationType ?? 'production-unit'

  if (!params.payload.farmId) {
    return 'selectFacility'
  }

  if (targetLocationType === 'processing-site') {
    const processingSiteId = params.payload.processingSiteId ?? ''

    if (!processingSiteId) {
      return 'selectSite'
    }

    if (params.payload.farmId === params.sourceLot.farmId) {
      if (
        params.sourceLot.locationType === 'processing-site' &&
        params.sourceLot.locationId === processingSiteId
      ) {
        return 'sameLocation'
      }

      return 'moveInstead'
    }

    return 'ready'
  }

  if (targetLocationType === 'farm' && params.payload.farmId !== params.sourceLot.farmId) {
    return 'ready'
  }

  if (!params.payload.productionUnitId) {
    return 'selectUnit'
  }

  if (
    params.payload.farmId === params.sourceLot.farmId &&
    params.payload.productionUnitId === params.sourceLot.productionUnitId
  ) {
    return 'sameLocation'
  }

  if (params.payload.farmId === params.sourceLot.farmId) {
    return 'moveInstead'
  }

  return 'ready'
}

export const buildLotFormPresentation = (params: BuildLotFormPresentationParams): LotFormPresentation => {
  const action = getLotFormAction(params.sourceLot, params.payload, {
    targetLocationType: params.targetLocationType,
    processingSiteId: params.formProcessingSiteId
  })
  const resolvedTargetLocationType = params.targetLocationType ?? 'production-unit'
  const resolvedTargetLocationId = resolvedTargetLocationType === 'processing-site'
    ? (params.formProcessingSiteId ?? '')
    : resolvedTargetLocationType === 'farm'
      ? params.payload.farmId
      : params.payload.productionUnitId
  const locationSummary = params.sourceLot && (action === 'move' || action === 'transfer')
    ? {
        currentFarmId: params.sourceLot.farmId,
        currentProductionUnitId: params.sourceLot.productionUnitId ?? '',
        targetFarmId: params.payload.farmId,
        targetProductionUnitId: params.payload.productionUnitId,
        targetLocationType: action === 'transfer'
          ? resolvedTargetLocationType
          : action === 'move' && resolvedTargetLocationType === 'processing-site'
            ? 'processing-site'
            : 'production-unit',
        targetLocationId: action === 'transfer'
          ? resolvedTargetLocationId
          : action === 'move' && resolvedTargetLocationType === 'processing-site'
            ? resolvedTargetLocationId
            : params.payload.productionUnitId
      }
    : null

  const noticeKey = !params.formFarmId
    ? null
    : !params.formProductionUnitId && resolvedTargetLocationType === 'production-unit'
      ? 'lots.form.notice.selectUnit'
      : action === 'move'
        ? 'lots.form.notice.move'
        : action === 'transfer'
          ? resolvedTargetLocationType === 'farm'
            ? 'lots.form.notice.transferFacility'
            : resolvedTargetLocationType === 'processing-site'
              ? 'lots.form.notice.transferProcessingSite'
            : 'lots.form.notice.transfer'
          : null

  if (action === 'create') {
    return {
      action,
      titleKey: 'lots.form.createTitle',
      submitKey: 'lots.button.create',
      noticeKey,
      locationSummary
    }
  }

  if (action === 'move') {
    return {
      action,
      titleKey: 'lots.form.moveTitle',
      submitKey: 'lots.button.move',
      noticeKey,
      locationSummary
    }
  }

  if (action === 'transfer') {
    return {
      action,
      titleKey: 'lots.form.transferTitle',
      submitKey: 'lots.button.transfer',
      noticeKey,
      locationSummary
    }
  }

  return {
    action,
    titleKey: 'lots.form.editTitle',
    submitKey: 'lots.button.saveChanges',
    noticeKey,
    locationSummary
  }
}